import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface NavigationGuardOptions {
  hasUnsavedChanges: boolean
  onConfirmSave: () => Promise<void>
}

export function useNavigationGuard({ hasUnsavedChanges, onConfirmSave }: NavigationGuardOptions) {
  const location = useLocation()
  const navigate = useNavigate()
  const isNavigationAllowed = useRef(false)

  // Intercept programmatic navigation
  useEffect(() => {
    if (!hasUnsavedChanges) return

    const unblock = () => {
      // Store the original navigate function
      const originalNavigate = window.history.pushState.bind(window.history)

      // Override history.pushState to intercept programmatic navigation
      window.history.pushState = function (...args: Parameters<typeof originalNavigate>) {
        const [, , url] = args

        // Check if navigation is allowed or if we're staying on the same page
        if (isNavigationAllowed.current || url === location.pathname) {
          originalNavigate.apply(window.history, args)
          return
        }

        // Show confirmation dialog
        const handleNavigation = async () => {
          const shouldSave = confirm(
            'You have unsaved changes. Do you want to save them before leaving?'
          )
          if (shouldSave) {
            await onConfirmSave()
            isNavigationAllowed.current = true
            originalNavigate.apply(window.history, args)
            window.dispatchEvent(new PopStateEvent('popstate'))
            isNavigationAllowed.current = false
          } else {
            const shouldLeave = confirm('Are you sure you want to leave without saving?')
            if (shouldLeave) {
              isNavigationAllowed.current = true
              originalNavigate.apply(window.history, args)
              window.dispatchEvent(new PopStateEvent('popstate'))
              isNavigationAllowed.current = false
            }
          }
        }

        handleNavigation()
      }

      return () => {
        window.history.pushState = originalNavigate
      }
    }

    const cleanup = unblock()
    return cleanup
  }, [hasUnsavedChanges, onConfirmSave, location.pathname])

  // Intercept all link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Check if it's a link or a parent is a link
      let target = e.target as HTMLElement
      let link: HTMLAnchorElement | null = null

      // Find the anchor element
      while (target && target !== document.body) {
        if (target.tagName === 'A') {
          link = target as HTMLAnchorElement
          break
        }
        target = target.parentElement as HTMLElement
      }

      if (!link) return

      // Check if it's an internal navigation
      const href = link.getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('//')) return

      // Check if we're navigating away from the current page
      if (href !== location.pathname && hasUnsavedChanges) {
        e.preventDefault()
        e.stopPropagation()

        const handleNavigation = async () => {
          const shouldSave = confirm(
            'You have unsaved changes. Do you want to save them before leaving?'
          )
          if (shouldSave) {
            await onConfirmSave()
            isNavigationAllowed.current = true
            navigate(href)
            isNavigationAllowed.current = false
          } else {
            const shouldLeave = confirm('Are you sure you want to leave without saving?')
            if (shouldLeave) {
              isNavigationAllowed.current = true
              navigate(href)
              isNavigationAllowed.current = false
            }
          }
        }

        handleNavigation()
      }
    }

    // Add event listener with capture to intercept before React Router
    document.addEventListener('click', handleClick, true)

    return () => {
      document.removeEventListener('click', handleClick, true)
    }
  }, [hasUnsavedChanges, onConfirmSave, location.pathname, navigate])
}
